﻿import React, {useEffect, useRef, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkAuth } from '../Redux/Actions/AuthActions';
import Login from './Login.jsx';
import ProfileMenu from './ProfileMenu.jsx';
import ProfileMenuItems from './ProfileMenuItems.jsx';
import logo from './assets/new-logo.svg';
import search from './assets/search.svg';
import searchButton from './assets/search-button.svg';
import rightArrow from './assets/right-arrow.webp';
import '../Shared/styles/collectionNavDropdown.css';
import './styles/Header.css';
import {Link, NavLink, useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import {CustomButton} from "../Shared/components/CustomButton.jsx";
import { SignInForCollectionNavModal } from '../Shared/components/SignInPromptModals.jsx';
import { useAbortController } from "../Shared/hooks/useAbortController.js";
import { clampSearchQuery, SEARCH_QUERY_MAX_LENGTH } from '../Shared/constants/searchQuery.js';
import { useHasLoggedInBefore } from '../Shared/hooks/useHasLoggedInBefore.js';

const headerNavLinkClass = ({ isActive }) => (isActive ? 'active' : '');

function HeaderNavLink({ fullPageNavigation, to, onClick, children }) {
    if (fullPageNavigation) {
        return (
            <a href={to} onClick={onClick}>
                {children}
            </a>
        );
    }

    return (
        <NavLink to={to} className={headerNavLinkClass} onClick={onClick}>
            {children}
        </NavLink>
    );
}

function HeaderRouteLink({ fullPageNavigation, to, children, ...props }) {
    if (fullPageNavigation) {
        return (
            <a href={to} {...props}>
                {children}
            </a>
        );
    }

    return (
        <Link to={to} {...props}>
            {children}
        </Link>
    );
}

export default function Header({ searchValue, handleSearchClick, fullPageNavigation = false }) {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const [menuVisible, setMenuVisible] = useState(false);
    const [profileMenuVisible, setProfileMenuVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isSearchOpened, setIsSearchOpened] = useState(false);
    const [isMyCollectionOpened, setIsMyCollectionOpened] = useState(false);
    const [isMyProfileOpened, setIsMyProfileOpened] = useState(false);
    const documentClickListener = useRef(null)
    const navigate = useNavigate();
    const [inputSearchValue, setInputSearchValue] = useState(clampSearchQuery(searchValue || ''));
    const [isNeedSingInModalOpened, setIsNeedSingInModalOpened] = useState(false);
    const { nextSignal } = useAbortController();
    const hasLoggedInBefore = useHasLoggedInBefore();

    useEffect(() => {
        setInputSearchValue(clampSearchQuery(searchValue || ''));
    }, [searchValue])

    const toggleMenu = () => {
        const menu = document.getElementById('menu');

        if (menuVisible) {
            menu.classList.remove('visible');
            menu.classList.add('exit-left');
            menu.style.transition = 'transform 0.5s ease-in-out, width 0.5s ease-in-out';

            setTimeout(() => {
                menu.style.transition = undefined;
                menu.classList.remove('exit-left');
            }, 500);
        } else {
            menu.style.transition = 'transform 0.5s ease-in-out, width 0.5s ease-in-out';
            menu.classList.add('visible');
        }

        setMenuVisible(!menuVisible);

    };

    const openMyColection = () => {
        setTimeout(() => {
            if (!isMyCollectionOpened) {
                document.addEventListener('click', documentClickListener.current);
            }
        })
        setIsMyCollectionOpened(!isMyCollectionOpened);

    }

    const toggleMyProfile = () => {
        setIsMyProfileOpened(prev => !prev);
    }

    useEffect(() => {
        dispatch(checkAuth({ signal: nextSignal('check-auth') }));

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [dispatch]);

    useEffect(() => {
        documentClickListener.current = () => {
            setIsMyCollectionOpened(false);
            document.removeEventListener('click', documentClickListener.current);
        }

        const params = new URLSearchParams(location.search);
        setInputSearchValue(clampSearchQuery(params.get('searchQuery') || ''));
    }, [])

    function onHeaderSearch() {
        const searchPath = `/upcoming-lots?searchQuery=${encodeURIComponent(inputSearchValue)}`;
        if (!handleSearchClick) {
            if (fullPageNavigation) {
                window.location.assign(searchPath);
            } else {
                navigate(searchPath);
            }
        } else {
            handleSearchClick(inputSearchValue);
        }
    }

    function closeHeaderMenu() {
        const menu = document.getElementById('menu');
        if (menu) {
            menu.classList.remove('visible');
            menu.classList.remove('exit-left');
            menu.style.transition = undefined;
        }
        setMenuVisible(false);
        setIsMyCollectionOpened(false);
        setIsMyProfileOpened(false);
    }

    useEffect(() => {
        if (!isNeedSingInModalOpened) {
            return;
        }
        closeHeaderMenu();
    }, [isNeedSingInModalOpened]);

    function onSavedJewelryClick(e) {
        if (!isAuthenticated) {
            e.preventDefault();
            closeHeaderMenu();
            setIsNeedSingInModalOpened(true);
            return;
        }
        closeHeaderMenu();
    }

    function onFollowedBrandsClick(e) {
        if (!isAuthenticated) {
            e.preventDefault();
            closeHeaderMenu();
            setIsNeedSingInModalOpened(true);
            return;
        }
        closeHeaderMenu();
    }

    function onSearchInputKeyDown(e) {
        if (e.key === 'Enter') {
            onHeaderSearch();
        }
    }

    return (
        <header className={`header ${menuVisible ? 'visible' : ''}`}>
            <div className={'header__content' + (isSearchOpened ? " active" : '') + (isMyCollectionOpened || profileMenuVisible ? " collection-opened" : "")}>
                <div className="header__controls">
                    <div className={"header__logo-control"}>
                        <a href={"/"} className="header__logo">
                            <img src={logo} alt="Logo" width={122} height={48} />
                        </a>
                        <div className={"header__control"}>
                            <div className="header__button">
                                <button
                                        className={`header__menu-icon ${menuVisible ? 'open' : ''} button`}
                                        aria-label={menuVisible ? 'Close menu' : 'Open menu'}
                                        aria-expanded={menuVisible}
                                        onClick={toggleMenu}>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div id={'menu'} className={`header__actions-links`}>
                        <div className="mobile">
                            <div className={'header-menu__search'}>
                                <img
                                    src={search}
                                    alt="search"
                                />
                                <input
                                    type="search"
                                    id="header-search-mobile"
                                    name="header-search-mobile"
                                    placeholder="Search"
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                        <div className={`header__links ${menuVisible ? 'visible' : ''}`}>
                            <HeaderNavLink fullPageNavigation={fullPageNavigation} to="/" onClick={toggleMenu}>
                                Home
                                <img src={rightArrow} alt='arrow' className={"mobile"}/>
                            </HeaderNavLink>
                            <HeaderNavLink fullPageNavigation={fullPageNavigation} to="/about-us" onClick={toggleMenu}>
                                About us
                                <img src={rightArrow} alt='arrow' className={"mobile"}/>
                            </HeaderNavLink>
                            <HeaderNavLink fullPageNavigation={fullPageNavigation} to="/upcoming-lots" onClick={toggleMenu}>
                                Upcoming Lots
                                <img src={rightArrow} alt='arrow' className={"mobile"}/>
                            </HeaderNavLink>
                            <HeaderNavLink fullPageNavigation={fullPageNavigation} to="/past-lots" onClick={toggleMenu}>
                                Auction results
                                <img src={rightArrow} alt='arrow' className={"mobile"}/>
                            </HeaderNavLink>
                            <HeaderNavLink fullPageNavigation={fullPageNavigation} to="/pricing" onClick={toggleMenu}>
                                Pricing
                                <img src={rightArrow} alt='arrow' className={"mobile"}/>
                            </HeaderNavLink>
                            <HeaderNavLink fullPageNavigation={fullPageNavigation} to="/guides" onClick={toggleMenu}>
                                Guides
                                <img src={rightArrow} alt='arrow' className={"mobile"}/>
                            </HeaderNavLink>
                            <div onClick={openMyColection}>
                                <span className={'header-link__my-collection'}>
                                    <span className={'header-link__my-collection-trigger' + (isMyCollectionOpened ? ' is-open' : '')}>
                                        My Collection
                                        <svg className="closed" width="16" height="17" viewBox="0 0 16 17" fill="none" strokeOpacity="0.6" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M4 6.5L8 10.5L12 6.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <svg className="opened" width="16" height="17" viewBox="0 0 16 17" fill="none" strokeOpacity="0.6" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M4 6.5L8 10.5L12 6.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </span>
                                    <div className={"collection__menu header__links" + (isMyCollectionOpened ? ' visible' : '')}>
                                        <HeaderRouteLink fullPageNavigation={fullPageNavigation} to="/my-collection" rel="nofollow" onClick={onSavedJewelryClick}>Saved Jewelry</HeaderRouteLink>
                                        <div className="divider"></div>
                                        <HeaderRouteLink fullPageNavigation={fullPageNavigation} to="/followed-brands" rel="nofollow" onClick={onFollowedBrandsClick}>Followed Brands</HeaderRouteLink>
                                    </div>
                                </span>
                            </div>
                            {isAuthenticated && (
                                <div className="header__my-profile">
                                    <div className="header-link__my-profile">
                                        <div className={'header-link__my-profile-trigger' + (isMyProfileOpened ? ' is-open' : '')} onClick={toggleMyProfile}>
                                            My Profile
                                            <svg className="closed" width="16" height="17" viewBox="0 0 16 17" fill="none" strokeOpacity="0.6" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M4 6.5L8 10.5L12 6.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <svg className="opened" width="16" height="17" viewBox="0 0 16 17" fill="none" strokeOpacity="0.6" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M4 6.5L8 10.5L12 6.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <div
                                            className={"profile__menu header__profile-dropdown" + (isMyProfileOpened ? ' visible' : '')}
                                        >
                                            <ProfileMenuItems onItemClick={toggleMenu}/>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <HeaderNavLink fullPageNavigation={fullPageNavigation} to="/contact-us" onClick={toggleMenu}>
                                Contact us
                                <img src={rightArrow} alt='arrow' className={"mobile"}/>
                            </HeaderNavLink>
                        </div>
                        {!isAuthenticated && (
                            <div className="header__mobile-auth">
                                {hasLoggedInBefore ? (
                                    <CustomButton
                                        text="Log In"
                                        styleType="tertiary"
                                        onClick={() => {
                                            toggleMenu();
                                            if (fullPageNavigation) {
                                                window.location.assign('/login');
                                            } else {
                                                navigate('/login');
                                            }
                                        }}
                                    />
                                ) : (
                                    <CustomButton
                                        text="Sign Up"
                                        styleType="tertiary"
                                        onClick={() => {
                                            toggleMenu();
                                            if (fullPageNavigation) {
                                                window.location.assign('/signup');
                                            } else {
                                                navigate('/signup');
                                            }
                                        }}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <div className={"desktop header__buttons"}>
                        <img
                            className={"header-search__button"}
                            src={search}
                            alt="search"
                            onClick={() => setIsSearchOpened(!isSearchOpened)}
                        />
                        {isAuthenticated ? (
                            <ProfileMenu profileMenuVisible={profileMenuVisible}
                                         setProfileMenuVisible={setProfileMenuVisible}/>
                        ) : (
                            <Login/>
                        )}
                    </div>
                </div>

                <div className={"header__search"}>
                    <div className="input-wrapper">
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.5 14.5L11.1167 11.1167M12.9442 6.72221C12.9442 10.1586 10.1585 12.9444 6.72209 12.9444C3.28572 12.9444 0.5 10.1586 0.5 6.72221C0.5 3.28578 3.28572 0.5 6.72209 0.5C10.1585 0.5 12.9442 3.28578 12.9442 6.72221Z" stroke="#E4E2D7" strokeOpacity="0.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input
                            type="search"
                            id="header-search"
                            name="searchQuery"
                            onKeyDown={onSearchInputKeyDown}
                            value={inputSearchValue}
                            onChange={(e) => setInputSearchValue(clampSearchQuery(e.target.value))}
                            maxLength={SEARCH_QUERY_MAX_LENGTH}
                            placeholder="Search"
                            autoComplete="off"
                        />
                    </div>
                    <button onClick={onHeaderSearch}>
                        <img
                            src={searchButton}
                            alt="search"
                        />
                    </button>
                </div>
            </div>
            <SignInForCollectionNavModal isOpened={isNeedSingInModalOpened} setIsOpened={setIsNeedSingInModalOpened}/>
        </header>
    );
}

Header.propTypes = {
    searchValue: PropTypes.string,
    handleSearchClick: PropTypes.func,
    fullPageNavigation: PropTypes.bool,
}